import { useState } from 'react';
import { useEffect } from 'react';

import { Clock01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Buffer } from 'buffer';
import cn from 'classnames';
import { sameOriginUrl } from 'helpers/safe-redirect';
import patternValidators from 'helpers/validators/pattern-validators';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useQueryParams } from 'hooks/query-params';
import { useSingleSignOnRedirect } from 'hooks/single-sign-on-redirect';
import ReactGA from 'react-ga4';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { SingleSignOnButton } from './components/SingleSignOnButton/SingleSignOnButton';
import { CodeInput } from 'components/CodeInput/CodeInput';
import { Button, FormMessage, Input, InputGroup, MessageType } from 'components/FormComponents';
import { GoogleButton } from 'components/GoogleButton/GoogleButton';
import { Loading } from 'components/Loading/Loading';

import { login, loginWithGoogle, selectAuthentication, verify2fa } from 'api/authentication-slice';

import styles from './SignIn.module.scss';

const SignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { trackEvent } = useTracking();
  const redirectToSingleSignOn = useSingleSignOnRedirect();

  const authentication = useSelector(selectAuthentication);

  useDocumentTitle('Sign In');

  const navigate = useNavigate();

  const [redirect_uri, state, reason] = useQueryParams(['redirect_uri', 'state', 'reason']);

  const [loading, setLoading] = useState(false);
  const [messageText, setMessageText] = useState();
  const [messageType, setMessageType] = useState(MessageType.Error);

  const [mfa, setMfa] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: 'Sign In',
    });
  }, []);

  useEffect(() => {
    if (!authentication.checked) return;
    if (authentication.logged) redirect();
  }, [authentication]);

  useEffect(() => {
    if (reason === 'email_verified') {
      setMessageType(MessageType.Success);
      setMessageText(t('signIn.emailVerified'));
      return;
    }

    if (reason === 'verification_link_invalid') {
      setMessageType(MessageType.Error);
      setMessageText(t('signIn.verificationLinkInvalid'));
      return;
    }

    if (reason !== 'session_expired') return;

    setMessageType(MessageType.Info);
    setMessageText(
      <span className="d-flex align-items-center u-gap">
        <HugeiconsIcon icon={Clock01Icon} size="20" />
        <span>
          <strong>{t('signIn.sessionExpiredTitle')}</strong> {t('signIn.sessionExpiredMessage')}
        </span>
      </span>,
    );
  }, [reason]);

  const redirect = () => {
    const query_params = state ? Buffer.from(state, 'base64').toString() : '';
    const destination = redirect_uri && sameOriginUrl(`${redirect_uri}${query_params}`);

    if (destination) {
      window.location.replace(destination);
    } else {
      navigate('/dashboard');
    }
  };

  const onGoogleSign = async ({ credential, clientId }) => {
    setLoading(true);
    const { payload: response } = await dispatch(
      loginWithGoogle({ credential, client_id: clientId }),
    );

    if (response.error) {
      setLoading(false);
      setMessageType(MessageType.Error);
      setMessageText(response.error.message);
    } else if (response.result.two_factor_auth_enabled) {
      setMessageText(null);
      setMfa(true);
    } else {
      redirect();
    }
  };

  const onGoogleSignFailure = error => {
    console.error(error);
    setMessageType(MessageType.Error);
    setMessageText('Error getting google profile');
  };

  const onSubmit = async data => {
    setLoading(true);
    if (await redirectToSingleSignOn(data.email)) return;

    const { payload: response } = await dispatch(
      login({ email: data.email, password: data.password }),
    );
    setLoading(false);

    if (response.error) {
      setMessageType(MessageType.Error);
      setMessageText(response.error.message);
      return;
    } else if (response.result.two_factor_auth_enabled) {
      setMessageText(null);
      setMfa(true);
    } else {
      redirect();
      trackEvent('User Signed In', {
        userId: response.result.user_id,
        userEmail: data.email,
        loginMethod: 'email',
      });
    }
  };

  const onMFASubmittion = async code => {
    setLoading(true);
    const { payload: response } = await dispatch(verify2fa({ code }));
    setLoading(false);

    if (response.error) {
      setMfa(false);
      setMessageType(MessageType.Error);
      setMessageText(response.error.message);
    } else if (response.result.verified) redirect();
    else {
      setMessageType(MessageType.Error);
      setMessageText(t('signIn.twoFactorAuthentication.invalidCode'));
    }
  };

  return (
    <div className={styles.root}>
      <Loading active={loading} />
      <div className={cn('card u-margin-top', 'card-with-shadow', styles.card)}>
        <div className={cn('card_content', styles.content, { [styles.mfaContent]: mfa })}>
          <img
            onClick={() => window.location.replace('https://www.checkmate.tech')}
            className={cn(styles.logo, 'u-margin-bottom--large')}
            src="/assets/images/checkmate/icon-label.svg"
            alt="KnowMy Logo"
          />
          {mfa ? (
            <>
              <h2
                className={cn(
                  styles.mfaTitle,
                  'u-margin-bottom--large u-margin-top--0 u-text-centered',
                )}>
                {t('signIn.twoFactorAuthentication.code')}
              </h2>
              <p className={cn(styles.mfaDescription, 'u-text-centered')}>
                {t('signIn.twoFactorAuthentication.description')}
              </p>
              <CodeInput
                error={!!messageText}
                dataTestId="sign-in-two-factor-code-input"
                onChange={() => setMessageText(null)}
                onComplete={onMFASubmittion}
              />
              <div className={styles.mfaErrorSlot}>
                <FormMessage
                  message={messageText}
                  type={messageType}
                  className="u-margin-top"
                  closeable={false}
                  dataTestId="sign-in-error-message"
                />
              </div>
            </>
          ) : (
            <>
              <h2 className="u-margin-bottom--large u-margin-top--0">
                <span className="display-2 u-font-weight-600 brand-gradient-text">
                  {t('common.welcome')}&nbsp;
                </span>
                <span className="display-2 u-font-weight-400">{t('common.back')}</span>
              </h2>
              <GoogleButton onLoginSuccess={onGoogleSign} onLoginFailure={onGoogleSignFailure} />
              <SingleSignOnButton className="u-margin-top" onClick={() => navigate('/sso')} />
              <div className={styles.divider}>
                <p>{t('common.or')}</p>
                <hr />
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="u-width-100 u-flex-box u-flex-align-center u-flex-direction-vertically">
                <FormMessage
                  message={messageText}
                  type={messageType}
                  noIcon={messageType === MessageType.Info}
                  dataTestId={
                    messageType === MessageType.Info
                      ? 'sign-in-session-expired-message'
                      : 'sign-in-error-message'
                  }
                  className="u-margin-top--0"
                  onClose={() => setMessageText(null)}
                />
                <InputGroup title="Email">
                  <Input
                    name="email"
                    icon="mail"
                    dataTestId="sign-in-email-input"
                    placeholder={t('signIn.emailPlaceholder')}
                    className="u-margin-bottom"
                    register={register}
                    validators={{ required: true, ...patternValidators.email }}
                    error={errors?.email?.message || errors?.email?.type}
                    inputProps={{ tabIndex: 1 }}
                  />
                </InputGroup>
                <InputGroup title="Password">
                  <Link
                    className={cn('u-link', 'dark', 'u-margin-top--small', styles.forgotPassword)}
                    to="/forgot_password">
                    {t('signIn.forgotPassword')}
                  </Link>
                  <Input
                    name="password"
                    icon="lock"
                    type="password"
                    dataTestId="sign-in-password-input"
                    placeholder={t('signIn.enterPassword')}
                    register={register}
                    validators={{ required: true }}
                    error={errors?.password?.type}
                    inputProps={{ tabIndex: 2 }}
                  />
                </InputGroup>
                <Button
                  submit
                  dataTestId="sign-in-submit-button"
                  buttonProps={{ tabIndex: 3 }}
                  className="u-width-100 u-margin-top--large">
                  {t('signIn.login')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
